import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import classNames from './ChangeLevelPopup.module.scss';
import ChooseLevel from './ChooseLevel';
import Successes from './Successes';
import PartialSuccesses from './PartialSuccesses';
import Failure from './Failure';
import BulkProgress from '@/components/school-management/BulkAction/BulkProgress/BulkProgress';
import { StudentReportItem } from '@/school-management/types/response';
import AppModal from '@/components/AppModal/AppModal';
import { Option } from '@/components/school-management/DropDown/DropDown';
import {
  BulkOperationResponseData,
  BulkOperationState,
  ChangeStudentsLevelBulkOperationItem,
} from '@/school-management/types/services/bulk';
import { bulkService } from '@/school-management/services/bulk';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { getLevelApiValue } from '@/school-management/utils/bulkLevelChange';

type ViewType = 'choose_level' | 'successes' | 'partial_successes' | 'failure' | 'progress';

interface ChangeLevelPopupProps {
  show?: boolean;
  students: StudentReportItem[];
  hasNonActivedStudents?: boolean;
  onClose: (changedStudents: StudentReportItem[]) => void;
}

const ChangeLevelPopup = ({
  show,
  students,
  hasNonActivedStudents,
  onClose,
}: ChangeLevelPopupProps) => {
  const { user } = useAppSelector(authSelector);
  const [view, setView] = useState<ViewType>('choose_level');
  const [selectedLevel, setSelectedLevel] = useState<Option>();
  const [operationId, setOperationId] = useState<string>('');
  const [bulkOperation, setBulkOperation] = useState<BulkOperationResponseData>();

  useEffect(() => {
    if (show) {
      setView('choose_level');
      setSelectedLevel(undefined);
      setOperationId('');
      setBulkOperation(undefined);
    }
  }, [show]);

  const closePopup = useCallback(() => {
    const studentsToProcess = students || [];

    if (view === 'successes') {
      onClose(studentsToProcess);
      return;
    }

    if (view === 'partial_successes') {
      const bulkStudentsMapper = (
        (bulkOperation?.results || []) as ChangeStudentsLevelBulkOperationItem[]
      ).reduce(
        (acc, s) => {
          if (s.studentId) {
            acc[s.studentId] = s;
          }

          return acc;
        },
        {} as {
          [studentId: string]: ChangeStudentsLevelBulkOperationItem;
        }
      );

      onClose(
        studentsToProcess.filter(s =>
          s.userId in bulkStudentsMapper ? bulkStudentsMapper[s.userId].success : true
        )
      );
      return;
    }

    onClose([]);
  }, [onClose, students, bulkOperation, view]);

  const startProgress = (level: Option) => {
    if (!level) {
      return;
    }

    const studentsToProcess = students || [];

    if (studentsToProcess.length === 0) {
      setView('successes');
      return;
    }

    setView('progress');
    setOperationId('');

    const startChangeLevel = async () => {
      let operationId: string = '';

      try {
        const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
        const result = await bulkService.changeStudentsLevel(
          organizationId,
          studentsToProcess.map(s => s.userId),
          getLevelApiValue(Number(level.value))
        );
        operationId = result.data?.data?.operationId || '';
      } catch (e) {}

      setOperationId(operationId);
      if (!operationId) {
        setView('failure');
        setBulkOperation(undefined);
      }
    };
    startChangeLevel();
  };

  const chooseLevelView = useMemo(() => {
    return (
      <ChooseLevel
        students={students}
        hasNonActivedStudents={hasNonActivedStudents}
        onClose={closePopup}
        onLevelSelected={(level: Option) => {
          setSelectedLevel(level);
          startProgress(level);
        }}
      />
    );
  }, [students, hasNonActivedStudents, closePopup]);

  const successesView = useMemo(() => {
    return (
      <Successes
        students={students}
        newLevelLabel={selectedLevel?.label || ''}
        bulkOperation={bulkOperation}
        onClose={closePopup}
      />
    );
  }, [students, selectedLevel, bulkOperation, closePopup]);

  const partialSuccessesView = useMemo(() => {
    return (
      <PartialSuccesses
        students={students}
        newLevelLabel={selectedLevel?.label || ''}
        bulkOperation={bulkOperation}
        onClose={closePopup}
      />
    );
  }, [students, selectedLevel, bulkOperation, closePopup]);

  const failureView = useMemo(() => {
    return (
      selectedLevel && (
        <Failure
          students={students}
          newLevelLabel={selectedLevel.label || ''}
          bulkOperation={bulkOperation}
          onClose={closePopup}
        />
      )
    );
  }, [students, selectedLevel, bulkOperation, closePopup]);

  const handleBulkOperationFinish = useCallback((bulkOperation: BulkOperationResponseData) => {
    setBulkOperation(bulkOperation);

    switch (bulkOperation?.state) {
      case BulkOperationState.Completed:
        setView('successes');
        break;
      case BulkOperationState.PartialSuccess:
        setView('partial_successes');
        break;
      case BulkOperationState.Failed:
        setView('failure');
        break;
    }
  }, []);

  const bulkProgressView = useMemo(() => {
    return <BulkProgress operationId={operationId} onFinish={handleBulkOperationFinish} />;
  }, [operationId, handleBulkOperationFinish]);

  const content = useMemo(() => {
    switch (view) {
      case 'choose_level':
        return chooseLevelView;
      case 'successes':
        return successesView;
      case 'partial_successes':
        return partialSuccessesView;
      case 'failure':
        return failureView;
      case 'progress':
        return bulkProgressView;
    }
  }, [view, chooseLevelView, successesView, partialSuccessesView, failureView, bulkProgressView]);

  return (
    <AppModal
      show={show}
      className={classNames.modal}
      modalContentClassName={classnames(classNames.modalContent, {
        [classNames.progressModalContent]: view === 'progress',
      })}
      type='fade'
      blockBodyScroll={true}
    >
      {content}
    </AppModal>
  );
};

export default ChangeLevelPopup;
